// ============================================================================
// SHARED MUTATION UTILITIES
// Common onSuccess patterns for consistent cache invalidation
// ============================================================================

import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';

/**
 * Invalidate all cleaning task related queries for a property
 */
export const invalidateCleaningTasks = async (
  queryClient: QueryClient,
  propertyId: string,
  cleanerId?: string
) => {
  const promises = [
    queryClient.invalidateQueries({ queryKey: queryKeys.cleaningTasks.byProperty(propertyId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.reservations.byProperty(propertyId) }),
  ];

  if (cleanerId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.cleaningTasks.inProgress() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.cleaningTasks.upcoming(cleanerId) }),
      queryClient.refetchQueries({ queryKey: queryKeys.cleaningTasks.current(propertyId, cleanerId) })
    );
  }

  await Promise.all(promises);
};

/**
 * Invalidate property-related queries after property updates
 */
export const invalidatePropertyQueries = async (
  queryClient: QueryClient,
  propertyId?: string
) => {
  const promises = [
    queryClient.invalidateQueries({ queryKey: queryKeys.properties.all }),
  ];

  if (propertyId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.properties.detail(propertyId) })
    );
  }

  await Promise.all(promises);
};

/**
 * Invalidate cleaner assignment queries after assignment changes
 */
export const invalidateCleanerAssignments = async (
  queryClient: QueryClient,
  propertyId: string
) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.cleaners.assignments(propertyId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.cleaningTasks.byProperty(propertyId) }),
  ]);
};

/**
 * Invalidate inventory queries after inventory changes
 */
export const invalidateInventoryQueries = async (
  queryClient: QueryClient,
  propertyId: string
) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.inventory.byProperty(propertyId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.inventory.replenishmentSuggestions(propertyId) }),
    queryClient.invalidateQueries({ queryKey: ["property-dashboard", propertyId] }),
  ]);
};

/**
 * Invalidate maintenance queries after issue changes
 */
export const invalidateMaintenanceQueries = async (
  queryClient: QueryClient,
  propertyId?: string
) => {
  const promises = [
    queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all }),
  ];

  if (propertyId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.byProperty(propertyId) })
    );
  }

  await Promise.all(promises);
};

/**
 * Invalidate vendor queries after vendor CRUD
 */
export const invalidateVendorQueries = async (
  queryClient: QueryClient,
  propertyId?: string
) => {
  const promises = [
    queryClient.invalidateQueries({ queryKey: queryKeys.vendors.all }),
  ];
  if (propertyId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.vendors.byProperty(propertyId) })
    );
  }
  await Promise.all(promises);
};

/**
 * Invalidate submission queries after submission changes
 */
export const invalidateSubmissionQueries = async (
  queryClient: QueryClient,
  propertyId?: string
) => {
  const promises = [
    queryClient.invalidateQueries({ queryKey: queryKeys.submissions.all }),
  ];

  if (propertyId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.byProperty(propertyId) })
    );
  }

  await Promise.all(promises);
};

/**
 * Invalidate photo queries after photo changes (checklist-scoped when checklistId provided)
 */
export const invalidatePhotoQueries = async (
  queryClient: QueryClient,
  propertyId: string,
  checklistId: string | null = null
) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.photos.reference(propertyId, checklistId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.photos.taskReference(propertyId, checklistId) }),
  ]);
};

/**
 * Invalidate company cleaning assignment queries
 */
export const invalidateCompanyAssignment = async (
  queryClient: QueryClient,
  propertyId: string
) => {
  await queryClient.invalidateQueries({ 
    queryKey: queryKeys.companies.propertyAssignment(propertyId) 
  });
};

/**
 * Invalidate billing and purchase queries (cleaner + owner Billing tab)
 */
export const invalidatePurchaseQueries = async (
  queryClient: QueryClient,
  propertyId: string
) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.billing.purchases.byCleaner(propertyId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.billing.purchases.all }),
    queryClient.invalidateQueries({ queryKey: ["property-purchases", propertyId] }),
  ]);
};

/**
 * Invalidate archived properties queries
 */
export const invalidateArchivedProperties = async (
  queryClient: QueryClient,
  propertyId?: string
) => {
  const promises = [
    queryClient.invalidateQueries({ queryKey: queryKeys.properties.all }),
    queryClient.invalidateQueries({ queryKey: queryKeys.properties.archived }),
  ];

  if (propertyId) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.properties.detail(propertyId) })
    );
  }

  await Promise.all(promises);
};
